import { useCallback, useEffect, useState } from "react"
import { AlertTriangle, CheckCircle2, Clock3, RefreshCw, Signal, Users, XCircle, ShieldAlert } from "lucide-react"
import { apiGet, apiPost, API_ENDPOINTS } from "../config/api"
import { useLanguage } from "../i18n/LanguageContext"

const initialData = { counters: {}, alerts: [], generated_at: null }

export default function OperationalControl() {
  const { t } = useLanguage()
  const [data, setData] = useState(initialData)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState("")
  const [risk, setRisk] = useState({ summary: {}, risks: [] })

  const createIncident = async (item) => {
    try {
      await apiPost(API_ENDPOINTS.ADMIN_INCIDENTS, {
        booking_id: item.booking_id,
        captain_name: item.captain_name,
        passenger_name: item.passenger_name,
        type: "trip_risk",
        severity: item.level === "critical" ? "critical" : item.level === "high" ? "high" : "warning",
        title: `Active trip risk #${item.booking_id}`,
        details: item.reasons.map((reason) => t(`operations.riskReasons.${reason}`)).join(" · ")
      })
      setError("")
    } catch (requestError) {
      setError(requestError.message || t("operations.incidentCreateFailed"))
    }
  }

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true)
    else setLoading(true)
    try {
      const [response, riskResponse] = await Promise.all([
        apiGet(API_ENDPOINTS.DASHBOARD_OPERATIONS_SUMMARY),
        apiGet(API_ENDPOINTS.OPERATIONS_RISK)
      ])
      setData(response || initialData)
      setRisk(riskResponse || { summary: {}, risks: [] })
      setError("")
    } catch (requestError) {
      setError(requestError.message || t("operations.loadFailed"))
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [t])

  useEffect(() => {
    load()
    const timer = window.setInterval(() => load(true), 15000)
    return () => window.clearInterval(timer)
  }, [load])

  const counters = data.counters || {}
  const cards = [
    { key: "activeTrips", value: counters.active_trips, icon: Signal, color: "blue" },
    { key: "unassignedBookings", value: counters.unassigned_bookings, icon: Clock3, color: "amber" },
    { key: "onlineCaptains", value: counters.online_captains, icon: Users, color: "green" },
    { key: "staleCaptains", value: counters.stale_captains, icon: AlertTriangle, color: "red" },
    { key: "pendingCaptains", value: counters.pending_captains, icon: Clock3, color: "orange" },
    { key: "completedToday", value: counters.completed_today, icon: CheckCircle2, color: "emerald" },
    { key: "cancelledToday", value: counters.cancelled_today, icon: XCircle, color: "gray" }
  ]

  return (
    <section className="operations-page">
      <div className="management-header">
        <div>
          <h2 className="management-title">{t("operations.title")}</h2>
          <p className="text-gray-500 mt-1">{t("operations.subtitle")}</p>
        </div>
        <button type="button" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50" onClick={() => load(true)} disabled={refreshing}>
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          {t("operations.refresh")}
        </button>
      </div>

      {error && <div className="p-4 mb-5 rounded-lg bg-red-50 border border-red-200 text-red-700">{error}</div>}
      {loading ? <div className="p-8 text-center text-gray-500">{t("common.loading")}</div> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {cards.map(({ key, value, icon: Icon, color }) => (
              <div key={key} className={`operations-card operations-card-${color}`}>
                <div className="operations-card-icon"><Icon className="w-5 h-5" /></div>
                <div><p className="text-sm text-gray-500">{t(`operations.${key}`)}</p><p className="text-2xl font-bold text-gray-900">{Number(value || 0).toLocaleString()}</p></div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mt-6 p-5">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div><h3 className="text-lg font-semibold text-gray-800">{t("operations.alerts")}</h3><p className="text-sm text-gray-500">{t("operations.alertsHint")}</p></div>
              {data.generated_at && <span className="text-xs text-gray-500">{new Date(data.generated_at).toLocaleTimeString()}</span>}
            </div>
            {data.alerts?.length ? <div className="space-y-3">{data.alerts.map((alert, index) => (
              <div key={`${alert.type}-${alert.booking_id || alert.captain_id || index}`} className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div><p className="font-medium text-gray-800">{alert.type === "stale_captain" ? t("operations.staleCaptain") : alert.type === "unassigned" ? t("operations.unassignedBooking") : t("operations.stuckBooking")}</p><p className="text-sm text-gray-600">{alert.captain_name || alert.booking_id || ""}{alert.minutes_since_heartbeat != null ? ` · ${alert.minutes_since_heartbeat} ${t("operations.minutes")}` : ""}</p></div>
              </div>
            ))}</div> : <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg p-4"><CheckCircle2 className="w-5 h-5" />{t("operations.noAlerts")}</div>}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mt-6 p-5">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div><h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-red-600" />{t("operations.tripRiskTitle")}</h3><p className="text-sm text-gray-500">{t("operations.tripRiskHint")}</p></div>
              <div className="flex gap-2 text-xs font-semibold"><span className="px-2 py-1 rounded bg-red-100 text-red-700">{t("operations.critical")}: {risk.summary?.critical || 0}</span><span className="px-2 py-1 rounded bg-orange-100 text-orange-700">{t("operations.high")}: {risk.summary?.high || 0}</span><span className="px-2 py-1 rounded bg-yellow-100 text-yellow-700">{t("operations.warning")}: {risk.summary?.warning || 0}</span></div>
            </div>
            {risk.risks?.length ? <div className="space-y-2">{risk.risks.filter((item) => item.level !== "normal").slice(0, 10).map((item) => <div key={item.booking_id} className="p-3 rounded-lg border border-gray-100 hover:border-amber-300 hover:bg-amber-50"><div className="flex items-center justify-between gap-3"><a href="/bookings" className="flex items-center gap-2"><span className={`px-2 py-1 rounded text-xs font-bold ${item.level === "critical" ? "bg-red-100 text-red-700" : item.level === "high" ? "bg-orange-100 text-orange-700" : "bg-yellow-100 text-yellow-700"}`}>{t(`operations.riskLevels.${item.level}`)}</span><span className="font-medium text-gray-800">#{item.booking_id} · {item.passenger_name || t("common.unknown")}</span></a><div className="flex items-center gap-3"><span className="text-sm font-semibold text-gray-600">{item.score}/100</span><button onClick={() => createIncident(item)} className="text-xs px-2 py-1 rounded bg-red-600 text-white">{t("operations.createIncident")}</button></div></div><p className="text-sm text-gray-600 mt-1">{item.reasons.map((reason) => t(`operations.riskReasons.${reason}`)).join(" · ")}</p></div>)}</div> : <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg p-4"><CheckCircle2 className="w-5 h-5" />{t("operations.noTripRisks")}</div>}
          </div>
        </>
      )}
    </section>
  )
}
